import { apiClient } from './client';

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'suspended' | 'trial';
  settings: Record<string, any>;
  features: { modules?: Record<string, boolean> };
  created_at: string;
  updated_at: string;
  // Computed fields
  user_count?: number;
  document_count?: number;
}

export interface CreateTenantInput {
  name: string;
  slug?: string;
  plan?: 'starter' | 'professional' | 'enterprise';
  status?: 'active' | 'suspended' | 'trial';
  settings?: Record<string, any>;
  // Admin user fields
  adminEmail?: string;
  adminPassword?: string;
  adminFirstName?: string;
  adminLastName?: string;
  sendWelcomeEmail?: boolean;
}

export interface UpdateTenantInput {
  name?: string;
  slug?: string;
  plan?: 'starter' | 'professional' | 'enterprise';
  status?: 'active' | 'suspended' | 'trial';
  settings?: Record<string, any>;
}

export interface TenantsResponse {
  success: boolean;
  data: Tenant[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface AdminUserInfo {
  id: string;
  email: string;
  cognitoUserId: string;
  temporaryPassword?: string;
}

export interface TenantWithAdmin extends Tenant {
  adminUser?: AdminUserInfo;
}

export interface TenantResponse {
  success: boolean;
  data: TenantWithAdmin;
}

export interface TenantUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'super_admin' | 'tenant_admin' | 'manager' | 'user';
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  phone?: string;
  jobTitle?: string;
  lastLoginAt?: string;
  createdAt: string;
}

export interface UpdateTenantUserInput {
  firstName?: string;
  lastName?: string;
  role?: 'tenant_admin' | 'manager' | 'user';
  status?: 'active' | 'inactive' | 'suspended';
  phone?: string;
  jobTitle?: string;
}

export interface CreateTenantUserInput {
  email: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  role?: 'tenant_admin' | 'manager' | 'user';
  phone?: string;
  jobTitle?: string;
  sendWelcomeEmail?: boolean;
}

export interface CreatedTenantUser extends TenantUser {
  temporaryPassword?: string;
}

export const tenantsApi = {
  // List all tenants (super admin only)
  list: async (params?: {
    limit?: number;
    offset?: number;
    status?: string;
    plan?: string;
    search?: string;
  }): Promise<TenantsResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.plan) searchParams.set('plan', params.plan);
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    const response = await apiClient.get(`/tenants${query ? `?${query}` : ''}`);
    return response.data;
  },

  // Get single tenant
  get: async (id: string): Promise<TenantResponse> => {
    const response = await apiClient.get(`/tenants/${id}`);
    return response.data;
  },

  // Create tenant
  create: async (data: CreateTenantInput): Promise<TenantResponse> => {
    const response = await apiClient.post('/tenants', data);
    return response.data;
  },

  // Update tenant
  update: async (id: string, data: UpdateTenantInput): Promise<TenantResponse> => {
    const response = await apiClient.put(`/tenants/${id}`, data);
    return response.data;
  },

  // Delete tenant (permanent=true removes all data, files, Cognito users)
  delete: async (id: string, permanent = true): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/tenants/${id}${permanent ? '?permanent=true' : ''}`);
    return response.data;
  },

  // Get tenant stats
  getStats: async (id: string): Promise<{
    success: boolean;
    data: {
      userCount: number;
      documentCount: number;
      departmentCount: number;
      storageUsed: number;
    };
  }> => {
    const response = await apiClient.get(`/tenants/${id}/stats`);
    return response.data;
  },

  // Get users for a specific tenant (super admin only)
  getUsers: async (tenantId: string, params?: {
    limit?: number;
    offset?: number;
    search?: string;
    status?: string;
    role?: string;
  }): Promise<{
    success: boolean;
    data: TenantUser[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.role) searchParams.set('role', params.role);

    const query = searchParams.toString();
    const response = await apiClient.get(`/tenants/${tenantId}/users${query ? `?${query}` : ''}`);
    return response.data;
  },

  // Reset user password (super admin only)
  resetUserPassword: async (tenantId: string, userId: string, password?: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post(`/tenants/${tenantId}/users/${userId}/reset-password`, password ? { password } : {});
    return response.data;
  },

  // Update user (super admin only)
  updateUser: async (tenantId: string, userId: string, data: UpdateTenantUserInput): Promise<{ success: boolean; data: TenantUser }> => {
    const response = await apiClient.put(`/tenants/${tenantId}/users/${userId}`, data);
    return response.data;
  },

  // Delete user (super admin only)
  deleteUser: async (tenantId: string, userId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/tenants/${tenantId}/users/${userId}`);
    return response.data;
  },

  // Create user (super admin only)
  createUser: async (tenantId: string, data: CreateTenantUserInput): Promise<{ success: boolean; data: CreatedTenantUser }> => {
    const response = await apiClient.post(`/tenants/${tenantId}/users`, data);
    return response.data;
  },

  // Update tenant modules (super admin only)
  updateModules: async (tenantId: string, modules: Record<string, boolean>): Promise<TenantResponse> => {
    const response = await apiClient.put(`/tenants/${tenantId}/modules`, { modules });
    return response.data;
  },

  // Get integration settings for a tenant (super admin only)
  getIntegrations: async (tenantId: string): Promise<{ success: boolean; data: Record<string, IntegrationSetting[]> }> => {
    const response = await apiClient.get(`/tenants/${tenantId}/integrations`);
    return response.data;
  },

  // Set an integration setting for a tenant (super admin only)
  setIntegration: async (tenantId: string, integration: string, key: string, value: string): Promise<void> => {
    await apiClient.put(`/tenants/${tenantId}/integrations`, { integration, key, value });
  },

  // Delete an integration setting for a tenant (super admin only)
  deleteIntegration: async (tenantId: string, integration: string, key: string): Promise<void> => {
    await apiClient.delete(`/tenants/${tenantId}/integrations/${integration}/${key}`);
  },

  // Test integration connection for a tenant (super admin only)
  testIntegration: async (tenantId: string, integration: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post(`/tenants/${tenantId}/integrations/test`, { integration });
    return response.data;
  },

  // Connect Xero for a tenant (super admin only) - initiates OAuth flow
  connectXero: async (tenantId: string, returnUrl?: string): Promise<{ authUrl: string }> => {
    const response = await apiClient.post(`/tenants/${tenantId}/integrations/xero/connect`, { returnUrl });
    return response.data.data;
  },

  // Get Xero connection status for a tenant (super admin only)
  getXeroStatus: async (tenantId: string): Promise<{ connected: boolean; connectedBy?: string; organizations?: any[]; status?: string; errorMessage?: string }> => {
    const response = await apiClient.get(`/tenants/${tenantId}/integrations/xero/status`);
    return response.data.data;
  },

  // Disconnect Xero for a tenant (super admin only)
  disconnectXero: async (tenantId: string): Promise<void> => {
    await apiClient.delete(`/tenants/${tenantId}/integrations/xero/disconnect`);
  },
};

export interface IntegrationSetting {
  key: string;
  isConfigured: boolean;
  isActive: boolean;
  maskedValue?: string;
  updatedAt?: string;
  updatedBy?: string;
}
